import { getApiUrl } from "@dashboard/config";
import {
  getSaleorRefreshToken,
  getStaffAccessToken,
  isExternalSession,
} from "@dashboard/services/saleorStaffToken";

import { CmsMedia, CmsPage, CmsPageInput, CmsPageListResponse } from "./types";

const getCmsApiBaseUrl = () => process.env.CMS_API_BASE_URL || "http://localhost:3001";

const getStorefrontUrl = () =>
  process.env.NEXT_PUBLIC_STOREFRONT_BASE_URL?.trim().replace(/\/+$/, "");

const MISSING_SESSION = "Missing Saleor session. Sign in again before managing CMS pages.";

/**
 * What the CMS backend is told about who is calling (WSM6-3065, DEVOPS-300).
 *
 * On a password session, exactly what it has always been: the refresh token
 * itself, which that backend trades for an access token of its own and checks
 * `MANAGE_PAGES` against. A Web Shop Manager session's refresh token belongs to
 * the sign-in plugin and that backend cannot trade it, so it gets a short-lived
 * access token as an ordinary bearer credential instead, minted from the one
 * getter in `src/services/saleorStaffToken`.
 *
 * `x-saleor-api-url` rides both, because it is how that backend knows which
 * tenant is calling and not part of the credential.
 */
const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const storefrontUrl = getStorefrontUrl();
  const storefrontHeader: Record<string, string> = storefrontUrl
    ? { "x-storefront-url": storefrontUrl }
    : {};

  if (isExternalSession()) {
    const token = await getStaffAccessToken();

    if (!token) {
      throw new Error(MISSING_SESSION);
    }

    return {
      "x-saleor-api-url": getApiUrl(),
      Authorization: `Bearer ${token}`,
      ...storefrontHeader,
    };
  }

  const refreshToken = getSaleorRefreshToken();

  if (!refreshToken) {
    throw new Error(MISSING_SESSION);
  }

  return {
    "x-saleor-api-url": getApiUrl(),
    "x-saleor-refresh-token": refreshToken,
    ...storefrontHeader,
  };
};

const parseResponse = async <T>(response: Response): Promise<T> => {
  const body = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(body?.error || `CMS request failed with ${response.status}`);
  }

  return body;
};

const saleorGraphqlRequest = async <T>(
  body: Record<string, unknown>,
  token?: string,
): Promise<T> => {
  const response = await fetch(getApiUrl(), {
    method: "POST",
    headers: {
      "content-type": "application/json",
      ...(token ? { "authorization-bearer": token } : {}),
    },
    body: JSON.stringify(body),
  });

  return parseResponse<T>(response);
};

const parseHtmlWidgetFromMetadata = (key?: string | null, value?: string | null) => {
  if (!key?.startsWith("html_widget_") || !value) {
    return null;
  }

  try {
    return JSON.parse(value) as { slug?: string; isActive?: boolean };
  } catch {
    return null;
  }
};

const jsonRequest = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${getCmsApiBaseUrl()}${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...authHeaders,
      ...init?.headers,
    },
  });

  return parseResponse<T>(response);
};

export const listCmsPages = (params?: {
  search?: string;
  page?: number;
  status?: string;
  pageType?: string;
}) => {
  const searchParams = new URLSearchParams();

  if (params?.search) searchParams.set("search", params.search);

  if (params?.page) searchParams.set("page", String(params.page));

  if (params?.status) searchParams.set("status", params.status);

  if (params?.pageType) searchParams.set("pageType", params.pageType);

  const query = searchParams.toString();

  return jsonRequest<CmsPageListResponse>(`/api/admin/cms/pages${query ? `?${query}` : ""}`);
};

export const getCmsPage = (id: string) =>
  jsonRequest<CmsPage>(`/api/admin/cms/pages/${encodeURIComponent(id)}`);

export const createCmsPage = (input: CmsPageInput, draft: boolean) =>
  jsonRequest<CmsPage>(`/api/admin/cms/pages?draft=${draft ? "true" : "false"}`, {
    method: "POST",
    body: JSON.stringify(input),
  });

export const updateCmsPage = (id: string, input: CmsPageInput, draft: boolean) =>
  jsonRequest<CmsPage>(
    `/api/admin/cms/pages/${encodeURIComponent(id)}?draft=${draft ? "true" : "false"}`,
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
  );

export const deleteCmsPage = (id: string) =>
  jsonRequest<{ id: string }>(`/api/admin/cms/pages/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });

export const uploadCmsMedia = async (file: File, alt: string): Promise<CmsMedia> => {
  const formData = new FormData();

  formData.append("file", file);
  formData.append("alt", alt);

  const response = await fetch(`${getCmsApiBaseUrl()}/api/admin/cms/media`, {
    method: "POST",
    headers: await getAuthHeaders(),
    body: formData,
  });

  return parseResponse<CmsMedia>(response);
};

export const checkSaleorSlugCollision = async (slug: string) => {
  // The same getter as everything else here: this used to mint its own token
  // from the refresh key on every keystroke's worth of checking, which is the
  // duplicate WSM6-3065 AC1 removes. On a Web Shop Manager session it is the minted access
  // token, and this is the call that would otherwise silently answer "no
  // collision" for want of a Saleor session.
  const token = await getStaffAccessToken();

  if (!token) {
    return false;
  }

  const result = await saleorGraphqlRequest<{
    data?: {
      page?: { id: string; isPublished?: boolean | null } | null;
      shop?: {
        metadata?: Array<{ key?: string | null; value?: string | null }> | null;
      } | null;
    };
  }>(
    {
      query: `
        query CmsSlugCollision($slug: String!) {
          page(slug: $slug) {
            id
            isPublished
          }
          shop {
            metadata {
              key
              value
            }
          }
        }
      `,
      variables: { slug },
    },
    token,
  );

  if (result.data?.page?.id) {
    return true;
  }

  return Boolean(
    result.data?.shop?.metadata?.some(item => {
      const widget = parseHtmlWidgetFromMetadata(item.key, item.value);

      return widget?.slug === slug && widget.isActive !== false;
    }),
  );
};
